import type { ApiClient, IApiJsonRequest } from '../../ApiClient';
import type { IApiGetUserResponseBody } from '../Users';
import type {
  IApiCreateOrganizationRequestBody,
  IApiDeleteOrganizationResponseBody,
  IApiGetOrganizationAppsResponseBody,
  IApiGetOrganizationResponseBody,
  IApiGetOrganizationsResponseBody,
  IApiGetOrganizationUserResponseBody,
  IApiGetOrganizationUsersResponseBody,
  IApiOrganizationAttachAppRequestBody,
  IApiUpdateOrganizationRequestBody,
  IApiUpdateOrganizationUserRequestBody,
} from './OrganizationsApi.types';

export class OrganizationsApi {
  public constructor(private client: ApiClient) {}

  public async create(
    body: IApiCreateOrganizationRequestBody,
  ): Promise<IApiGetOrganizationResponseBody> {
    const request: IApiJsonRequest = {
      method: 'post',
      path: '/organizations',
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetOrganizationResponseBody>(
        request,
      );

    return data;
  }

  public async getAll(): Promise<IApiGetOrganizationsResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: '/organizations',
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetOrganizationsResponseBody>(
        request,
      );

    return data;
  }

  public async get(
    organizationId: string,
  ): Promise<IApiGetOrganizationResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/organizations/${organizationId}`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetOrganizationResponseBody>(
        request,
      );

    return data;
  }

  public async update(
    appId: string,
    body: IApiUpdateOrganizationRequestBody,
  ): Promise<IApiGetOrganizationResponseBody> {
    const request: IApiJsonRequest = {
      method: 'patch',
      path: `/organizations/${appId}`,
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetOrganizationResponseBody>(
        request,
      );

    return data;
  }

  public async delete(
    organizationId: string,
  ): Promise<IApiDeleteOrganizationResponseBody> {
    const request: IApiJsonRequest = {
      method: 'delete',
      path: `/organizations/${organizationId}`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiDeleteOrganizationResponseBody>(
        request,
      );

    return data;
  }

  public async getApps(
    organizationId: string,
  ): Promise<IApiGetOrganizationAppsResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/organizations/${organizationId}/apps`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetOrganizationAppsResponseBody>(
        request,
      );

    return data;
  }

  public async attachApp(
    organizationId: string,
    body: IApiOrganizationAttachAppRequestBody,
  ): Promise<IApiGetOrganizationResponseBody> {
    const request: IApiJsonRequest = {
      method: 'put',
      path: `/organizations/${organizationId}/apps`,
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetOrganizationResponseBody>(
        request,
      );

    return data;
  }

  public async detachApp(
    organizationId: string,
    appId: string,
  ): Promise<IApiGetOrganizationResponseBody> {
    const request: IApiJsonRequest = {
      method: 'delete',
      path: `/organizations/${organizationId}/apps/${appId}`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetOrganizationResponseBody>(
        request,
      );

    return data;
  }

  public async getUsers(
    organizationId: string,
  ): Promise<IApiGetOrganizationUsersResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/organizations/${organizationId}/users`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetOrganizationUsersResponseBody>(
        request,
      );

    return data;
  }

  public async getUser(
    organizationId: string,
    userId: string,
  ): Promise<IApiGetOrganizationUserResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/organizations/${organizationId}/users/${userId}`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetOrganizationUserResponseBody>(
        request,
      );

    return data;
  }

  public async updateUser(
    organizationId: string,
    userId: string,
    body: IApiUpdateOrganizationUserRequestBody,
  ): Promise<IApiGetUserResponseBody> {
    const request: IApiJsonRequest = {
      method: 'patch',
      path: `/organizations/${organizationId}/users/${userId}`,
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetUserResponseBody>(request);

    return data;
  }

  public async removeUser(
    organizationId: string,
    userId: string,
  ): Promise<IApiGetUserResponseBody> {
    const request: IApiJsonRequest = {
      method: 'delete',
      path: `/organizations/${organizationId}/users/${userId}`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetUserResponseBody>(request);

    return data;
  }
}
